import { ApiProduct } from '@vend/types'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import React from 'react'
import { useUserSwitchingService } from '../../ng-compat/hooks'
import { $rootScope } from '../../ng-compat/imports'
import { useCustomerCodeSearch } from './useCustomerCodeSearch'
import { useProductBarcodeSearch } from './useProductBarcodeSearch'
import { useProductCodeSearch } from './useProductCodeSearch'

const usiMatcher = /^usi:([0-9A-Za-z]){12}$/

export interface FeelingLuckyResult {
  product: ApiProduct | undefined
  handled: boolean
}

export const useGlobalSearchFeelingLucky = () => {
  const userSwitchingService = useUserSwitchingService()
  const searchByCustomerCode = useCustomerCodeSearch()
  const searchByProductBarcode = useProductBarcodeSearch()
  const searchByProductCode = useProductCodeSearch()

  return async (
    query: string,
    suggestionsAvailable: boolean
  ): Promise<FeelingLuckyResult> => {
    // Handle user switching queries with this format: usi:15113b8b3522
    if (isUserSwitchingID(query)) {
      // Open the user switching modal.
      userSwitchingService.fastSwitch({ switchId: query }).catch(() => {})

      // Don't select any product.
      return {
        handled: true,
        product: undefined,
      }
    }

    // Handle customer code queries. Example: asdf-UDX2
    const customer = await searchByCustomerCode(query)
    if (customer) {
      // This selects the customer.
      $rootScope.$emit('search:customer-selected', customer)

      // Don't select any products.
      return {
        handled: true,
        product: undefined,
      }
    }

    // Handle barcode product queries. Example: 2010000100004.
    // Part of the code could be the price or quantity/weight of the product.
    // That is the "barcodeData".
    const productBarcodeResult = await searchByProductBarcode(query)
    if (productBarcodeResult) {
      // Add the product using the barcode data if provided.
      $rootScope.$emit('search:embedded-barcode-selected', {
        product: productBarcodeResult.product,
        barcodeData: productBarcodeResult.barcodeData,
      })

      setTimeout(() => {
        $rootScope.$emit('autocomplete:global-search:focus')
      })

      // Don't select any additional products.
      return {
        handled: true,
        product: productBarcodeResult.product,
      }
    }

    // Search by product code. Example: 10000
    const productByCodeResult = await searchByProductCode(query)
    if (productByCodeResult) {
      // A product was found, select it.
      return {
        handled: false,
        product: productByCodeResult,
      }
    }

    // Nothing was found. If there are available suggestions, let the auto
    // complete select the current suggestion.
    if (suggestionsAvailable) {
      return {
        handled: false,
        product: undefined,
      }
    }

    ToastNotifications.negative(
      <T
        _str="No results found for “{query}”. Try searching by product name."
        query={query}
        _context={'Error message when searching for products or other entities'}
      />
    )

    // Prevent adding single character unknown product for "." & "+"
    if (query === '.' || query === '+') {
      return {
        handled: true,
        product: undefined,
      }
    }

    // Add an unknown product.
    $rootScope.$emit('search:unknown-product', {
      query,
      suggestions: [],
    })

    return {
      handled: true,
      product: undefined,
    }
  }
}

function isUserSwitchingID(query: string): boolean {
  return usiMatcher.test(query)
}
